import { Link } from '@tanstack/react-router';
import { format } from 'date-fns';
import { Loader2, Pencil, RotateCw, Trash2, UserPlus } from 'lucide-react';
import React, { type ReactNode, useState } from 'react';

import { EditTeamMemberModal } from '@apps/web/components/settings/edit_team_member_modal';
import { InviteUserModal } from '@apps/web/components/settings/invite_user_modal';
import { RoleSelectItem } from '@apps/web/components/settings/role_select_item';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    Select,
    SelectContent,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    canManageOrgSettings,
    isOrganizationAdmin,
    orgMemberRoleLabel,
} from '@apps/web/lib/organization_permissions';
import { PHOEBE_TEAM_USER_EMAIL } from '@apps/web/lib/phoebe_team_user';
import { formatPhoneNumber } from '@apps/web/lib/phone_number_formatting';
import { cn } from '@apps/web/lib/utils';
import {
    useOrganizationUsers,
    useUserProfile,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

type UserManagementProps = {
    readonly organizationId: string;
    readonly variant?: 'card' | 'embedded';
    readonly onUsersChange?: () => void | Promise<unknown>;
    // Title icon shown on the classic settings page; the redesigned settings
    // render this card without one.
    readonly icon?: ReactNode;
    // The redesigned Team page hides the Joined column; everywhere else
    // keeps it.
    readonly showJoinedColumn?: boolean;
    readonly enableNotificationSettings?: boolean;
};

export function UserManagement({
    organizationId,
    variant = 'card',
    onUsersChange,
    icon,
    showJoinedColumn = true,
    enableNotificationSettings = false,
}: UserManagementProps) {
    const {
        data: users,
        isLoading,
        mutate,
    } = useOrganizationUsers(organizationId, { reason: 'user-management' });
    const { data: userProfile, mutate: mutateUserProfile } = useUserProfile({
        reason: 'user-management-profile',
    });
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [removingUserId, setRemovingUserId] = useState<string | null>(null);
    const [resendingEmail, setResendingEmail] = useState<string | null>(null);
    const [updatingRoleUserId, setUpdatingRoleUserId] = useState<string | null>(
        null,
    );
    const [editingUserId, setEditingUserId] = useState<string | null>(null);
    const isEmbedded = variant === 'embedded';

    const currentUserId = userProfile?.user.id ?? null;
    // Team-member management (inviting, role changes, removal) is admin-only.
    const canManageMembers = isOrganizationAdmin(userProfile, organizationId);
    // Members' roles are visible to admins and schedulers; restricted
    // schedulers see the roster but not who holds which role.
    const canViewRoles = canManageOrgSettings(userProfile, organizationId);
    // Anyone can edit their own row from this column even without
    // canManageMembers, so the column itself must render for any signed-in
    // viewer, not just admins.
    const showActionsColumn = canManageMembers || currentUserId !== null;
    const showPhoneColumn = isEmbedded;
    // Keep the shared account available to other roster consumers while
    // omitting it from the customer-facing Team Members table.
    const visibleUsers = users?.filter(
        (user) => user.email !== PHOEBE_TEAM_USER_EMAIL,
    );
    const editingUser =
        editingUserId !== null
            ? (visibleUsers?.find((user) => user.id === editingUserId) ?? null)
            : null;

    const handleRoleChange = async (
        targetUserId: string,
        nextRole: 'admin' | 'scheduler' | 'restricted_scheduler',
    ) => {
        setUpdatingRoleUserId(targetUserId);
        try {
            await getApiClient().patch({
                url: '/organizations/{organization_id}/users/{user_id}',
                pathParams: {
                    organization_id: organizationId,
                    user_id: targetUserId,
                },
                body: { role: nextRole },
            });
            toast.success('Role updated', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
        } catch (error) {
            // Surface the API detail (e.g. last-admin guard) when present.
            const message =
                error instanceof Error
                    ? error.message
                    : 'Failed to update role';
            toast.error(message, {
                persistNotification: true,
                notificationType: 'settings_update',
            });
            setUpdatingRoleUserId(null);
            return;
        }

        try {
            await mutate();
            await onUsersChange?.();
        } catch {
            toast.error('Role updated, but failed to refresh team members', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
        } finally {
            setUpdatingRoleUserId(null);
        }
    };

    const formatName = (firstName: string | null, lastName: string | null) => {
        const parts = [firstName, lastName].filter(Boolean);
        return parts.length > 0 ? parts.join(' ') : '—';
    };

    const formatDate = (date: string | null) => {
        if (!date) {
            return '—';
        }
        return format(new Date(date), 'MMM d, yyyy');
    };

    const header = (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
                <CardTitle className={cn(icon && 'flex items-center gap-2')}>
                    {icon}
                    Team Members
                </CardTitle>
                <CardDescription>
                    {canManageMembers
                        ? 'Manage users in your organization'
                        : 'View users in your organization'}
                </CardDescription>
            </div>
            {canManageMembers && (
                <Button onClick={() => setInviteModalOpen(true)} size="sm">
                    <UserPlus className="mr-2 h-4 w-4" />
                    Invite User
                </Button>
            )}
        </div>
    );

    const content = (
        <React.Fragment>
            {isLoading ? (
                <div className="text-muted-foreground py-8 text-center">
                    Loading team members…
                </div>
            ) : visibleUsers && visibleUsers.length > 0 ? (
                <div className="rounded-md border">
                    <Table
                        className={
                            isEmbedded
                                ? 'min-w-[720px] md:min-w-0 md:table-fixed'
                                : undefined
                        }
                    >
                        {isEmbedded && (
                            <colgroup>
                                <col className="w-[18%]" />
                                <col className="w-[24%]" />
                                <col className="w-[16%]" />
                                {canViewRoles && <col className="w-[15%]" />}
                                <col className="w-[9%]" />
                                {showJoinedColumn && <col className="w-[9%]" />}
                                {showActionsColumn && (
                                    <col className="w-[9%]" />
                                )}
                            </colgroup>
                        )}
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Email</TableHead>
                                {showPhoneColumn && (
                                    <TableHead>Phone</TableHead>
                                )}
                                {canViewRoles && <TableHead>Role</TableHead>}
                                <TableHead>Status</TableHead>
                                {showJoinedColumn && (
                                    <TableHead>Joined</TableHead>
                                )}
                                {showActionsColumn && (
                                    <TableHead>Actions</TableHead>
                                )}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {visibleUsers.map((user, index) => {
                                const displayName = formatName(
                                    user.first_name,
                                    user.last_name,
                                );
                                const phoneNumber = formatPhoneNumber(
                                    user.phone_number,
                                );
                                // Rows with a user id are org members; rows
                                // without one are pending invitations. Status
                                // now reports whether a member has ever signed
                                // in, so it can no longer stand in for
                                // membership.
                                const isMember = user.id !== null;
                                const joinedDate = isMember
                                    ? formatDate(user.joined_at)
                                    : formatDate(user.invited_at);
                                return (
                                    <TableRow key={user.email + index}>
                                        <TableCell className="min-w-0 font-medium">
                                            {enableNotificationSettings &&
                                            canManageMembers &&
                                            user.id ? (
                                                <Link
                                                    to="/settings/team-members/$memberUserId/notifications"
                                                    params={{
                                                        memberUserId: user.id,
                                                    }}
                                                    className="hover:text-primary decoration-border block truncate underline underline-offset-4"
                                                    title={`Manage notifications for ${displayName}`}
                                                >
                                                    {displayName}
                                                </Link>
                                            ) : (
                                                <span
                                                    className="block truncate"
                                                    title={displayName}
                                                >
                                                    {displayName}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="min-w-0">
                                            <span
                                                className="block truncate"
                                                title={user.email}
                                            >
                                                {user.email}
                                            </span>
                                        </TableCell>
                                        {showPhoneColumn && (
                                            <TableCell className="text-muted-foreground min-w-0">
                                                <span
                                                    className="block truncate"
                                                    title={phoneNumber}
                                                >
                                                    {phoneNumber}
                                                </span>
                                            </TableCell>
                                        )}
                                        {canViewRoles && (
                                            <TableCell className="min-w-0">
                                                {isMember &&
                                                canManageMembers ? (
                                                    <Select
                                                        value={
                                                            user.role ??
                                                            'scheduler'
                                                        }
                                                        disabled={
                                                            updatingRoleUserId ===
                                                            user.id
                                                        }
                                                        onValueChange={(
                                                            value:
                                                                | 'admin'
                                                                | 'scheduler'
                                                                | 'restricted_scheduler',
                                                        ) => {
                                                            if (
                                                                !user.id ||
                                                                value ===
                                                                    (user.role ??
                                                                        'scheduler')
                                                            ) {
                                                                return;
                                                            }
                                                            void handleRoleChange(
                                                                user.id,
                                                                value,
                                                            );
                                                        }}
                                                    >
                                                        <SelectTrigger
                                                            className={cn(
                                                                'h-8',
                                                                isEmbedded
                                                                    ? 'w-full min-w-0'
                                                                    : 'w-[170px]',
                                                            )}
                                                        >
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <RoleSelectItem role="scheduler" />
                                                            <RoleSelectItem role="restricted_scheduler" />
                                                            <RoleSelectItem role="admin" />
                                                        </SelectContent>
                                                    </Select>
                                                ) : (
                                                    <span
                                                        className="block truncate"
                                                        title={orgMemberRoleLabel(
                                                            user.role,
                                                        )}
                                                    >
                                                        {orgMemberRoleLabel(
                                                            user.role,
                                                        )}
                                                    </span>
                                                )}
                                            </TableCell>
                                        )}
                                        <TableCell className="min-w-0">
                                            <Badge
                                                variant="secondary"
                                                className={
                                                    user.status === 'active'
                                                        ? 'border-transparent bg-green-100 text-green-700 capitalize dark:bg-green-950/50 dark:text-green-300'
                                                        : 'bg-muted text-foreground/80 border-transparent capitalize'
                                                }
                                            >
                                                {user.status}
                                            </Badge>
                                        </TableCell>
                                        {showJoinedColumn && (
                                            <TableCell className="text-muted-foreground min-w-0">
                                                <span
                                                    className="block truncate"
                                                    title={joinedDate}
                                                >
                                                    {joinedDate}
                                                </span>
                                            </TableCell>
                                        )}
                                        {showActionsColumn && (
                                            <TableCell>
                                                <div className="flex items-center justify-end gap-1">
                                                    {isMember &&
                                                        (canManageMembers ||
                                                            user.id ===
                                                                currentUserId) && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                aria-label={`Edit ${displayName}`}
                                                                onClick={() =>
                                                                    setEditingUserId(
                                                                        user.id,
                                                                    )
                                                                }
                                                                className={
                                                                    isEmbedded
                                                                        ? 'h-8 w-8 p-0'
                                                                        : undefined
                                                                }
                                                            >
                                                                <Pencil className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                    {canManageMembers &&
                                                        isMember &&
                                                        user.id !==
                                                            currentUserId && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                disabled={
                                                                    removingUserId ===
                                                                    user.id
                                                                }
                                                                onClick={async () => {
                                                                    if (
                                                                        !user.id ||
                                                                        !confirm(
                                                                            `Remove ${user.email} from this organization?`,
                                                                        )
                                                                    ) {
                                                                        return;
                                                                    }

                                                                    setRemovingUserId(
                                                                        user.id,
                                                                    );
                                                                    try {
                                                                        await getApiClient().delete(
                                                                            {
                                                                                url: '/organizations/{organization_id}/users/{user_id}',
                                                                                pathParams:
                                                                                    {
                                                                                        organization_id:
                                                                                            organizationId,
                                                                                        user_id:
                                                                                            user.id,
                                                                                    },
                                                                            },
                                                                        );
                                                                        toast.success(
                                                                            'Member removed',
                                                                            {
                                                                                persistNotification: true,
                                                                                notificationType:
                                                                                    'settings_update',
                                                                            },
                                                                        );
                                                                    } catch {
                                                                        toast.error(
                                                                            'Failed to remove member',
                                                                            {
                                                                                persistNotification: true,
                                                                                notificationType:
                                                                                    'settings_update',
                                                                            },
                                                                        );
                                                                        setRemovingUserId(
                                                                            null,
                                                                        );
                                                                        return;
                                                                    }

                                                                    try {
                                                                        await mutate();
                                                                        await onUsersChange?.();
                                                                    } catch {
                                                                        toast.error(
                                                                            'Member removed, but failed to refresh team members',
                                                                            {
                                                                                persistNotification: true,
                                                                                notificationType:
                                                                                    'settings_update',
                                                                            },
                                                                        );
                                                                    } finally {
                                                                        setRemovingUserId(
                                                                            null,
                                                                        );
                                                                    }
                                                                }}
                                                                className={
                                                                    isEmbedded
                                                                        ? 'h-8 w-8 p-0'
                                                                        : undefined
                                                                }
                                                            >
                                                                {removingUserId ===
                                                                user.id ? (
                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                ) : (
                                                                    <Trash2 className="h-4 w-4" />
                                                                )}
                                                            </Button>
                                                        )}
                                                    {canManageMembers &&
                                                        !isMember && (
                                                            <Button
                                                                variant="ghost"
                                                                size={
                                                                    isEmbedded
                                                                        ? 'icon'
                                                                        : 'sm'
                                                                }
                                                                aria-label={`Resend invite to ${user.email}`}
                                                                title={
                                                                    isEmbedded
                                                                        ? `Resend invite to ${user.email}`
                                                                        : undefined
                                                                }
                                                                disabled={
                                                                    resendingEmail ===
                                                                    user.email
                                                                }
                                                                onClick={async () => {
                                                                    setResendingEmail(
                                                                        user.email,
                                                                    );
                                                                    try {
                                                                        await getApiClient().post(
                                                                            {
                                                                                url: '/organizations/{organization_id}/invitations/resend',
                                                                                pathParams:
                                                                                    {
                                                                                        organization_id:
                                                                                            organizationId,
                                                                                    },
                                                                                body: {
                                                                                    email: user.email,
                                                                                },
                                                                            },
                                                                        );
                                                                        toast.success(
                                                                            'Invitation resent',
                                                                            {
                                                                                persistNotification: true,
                                                                                notificationType:
                                                                                    'settings_update',
                                                                            },
                                                                        );
                                                                    } catch {
                                                                        toast.error(
                                                                            'Failed to resend invitation',
                                                                            {
                                                                                persistNotification: true,
                                                                                notificationType:
                                                                                    'settings_update',
                                                                            },
                                                                        );
                                                                    } finally {
                                                                        setResendingEmail(
                                                                            null,
                                                                        );
                                                                    }
                                                                }}
                                                                className={
                                                                    isEmbedded
                                                                        ? 'h-8 w-8'
                                                                        : undefined
                                                                }
                                                            >
                                                                {resendingEmail ===
                                                                user.email ? (
                                                                    <Loader2
                                                                        className={cn(
                                                                            'h-4 w-4 animate-spin',
                                                                            !isEmbedded &&
                                                                                'mr-1.5',
                                                                        )}
                                                                    />
                                                                ) : (
                                                                    <RotateCw
                                                                        className={cn(
                                                                            'h-4 w-4',
                                                                            !isEmbedded &&
                                                                                'mr-1.5',
                                                                        )}
                                                                    />
                                                                )}
                                                                {!isEmbedded &&
                                                                    'Resend Invite'}
                                                            </Button>
                                                        )}
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            ) : (
                <div className="text-muted-foreground py-8 text-center">
                    <p>No team members yet.</p>
                    {canManageMembers && (
                        <p className="mt-2 text-sm">
                            Invite your first team member to get started.
                        </p>
                    )}
                </div>
            )}
        </React.Fragment>
    );

    return (
        <React.Fragment>
            {variant === 'embedded' ? (
                <div className="space-y-5">
                    {canManageMembers && (
                        <div className="flex justify-stretch sm:justify-end">
                            <Button
                                className="w-full sm:w-auto"
                                onClick={() => setInviteModalOpen(true)}
                                size="sm"
                            >
                                <UserPlus className="mr-2 h-4 w-4" />
                                Invite User
                            </Button>
                        </div>
                    )}
                    {content}
                </div>
            ) : (
                <Card className="shadow-none">
                    <CardHeader>{header}</CardHeader>
                    <CardContent>{content}</CardContent>
                </Card>
            )}
            {canManageMembers && (
                <InviteUserModal
                    open={inviteModalOpen}
                    onOpenChange={setInviteModalOpen}
                    organizationId={organizationId}
                    onSuccess={async () => {
                        await mutate();
                        await onUsersChange?.();
                    }}
                />
            )}
            {editingUser && editingUser.id && (
                <EditTeamMemberModal
                    open={true}
                    onOpenChange={(nextOpen) => {
                        if (!nextOpen) {
                            setEditingUserId(null);
                        }
                    }}
                    organizationId={organizationId}
                    userId={editingUser.id}
                    isSelf={editingUser.id === currentUserId}
                    initialFirstName={editingUser.first_name}
                    initialLastName={editingUser.last_name}
                    initialPhoneNumber={editingUser.phone_number}
                    initialAssignedOfficeIds={
                        editingUser.assigned_office_ids ?? []
                    }
                    canEditOfficeScope={canManageMembers}
                    onSaved={async () => {
                        await mutate();
                        await onUsersChange?.();
                        if (editingUser.id === currentUserId) {
                            await mutateUserProfile();
                        }
                    }}
                />
            )}
        </React.Fragment>
    );
}
